<script setup>
import { computed, ref, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { mdiMagnify, mdiOpenInNew } from '@mdi/js'
import menu, { DIVIDER } from '@/menu.js'
import Icon from '@/components/Icon.vue'

const router = useRouter()
const { t } = useI18n()

const query = ref('')
const root = ref(null)
const inputEl = ref(null)

/* Flatten menu.js (strings are section headings, arrays are the links).
 *
 * Recurses one level into `item.menu`, because that is where most of the panel
 * now lives: the sidebar's Settings and Billing groups are collapsed by
 * default, and a flatten that only walked the top level made every route
 * inside them unfindable here — which is the whole point of this box. A nested
 * child is indexed under its parent's label, not the section heading, so
 * "Cart" reads as "Billing" rather than as whatever heading came last.
 *
 * A group that only toggles has no `to` and no `href`, so it never becomes a
 * result of its own — `go()` would have nowhere to send you. */
const entries = computed(() => {
  const flat = []
  let section = null

  const label = (item) => (item.labelKey ? t(item.labelKey) : item.label)

  const push = (item, itemSection) => {
    const text = label(item)
    if (!item || !text) return
    if (item.to || item.href) {
      // Store the resolved string: the filter below matches on what the user
      // can actually see, which in a non-English locale is not the key.
      flat.push({ ...item, label: text, section: itemSection })
    }
    if (Array.isArray(item.menu)) {
      item.menu.forEach(child => push(child, text))
    }
  }

  menu.forEach(group => {
    if (typeof group === 'string') {
      // DIVIDER is a rule in the sidebar, not a heading — it must not become
      // the section label for anything.
      if (group !== DIVIDER) section = group
      return
    }

    if (Array.isArray(group)) {
      group.forEach(item => push(item, section))

      /* A heading labels the one list that follows it, and nothing after that.
         Every heading in menu.js has always been followed by exactly one
         array, so this relabels nothing that existed before — but the
         top-level nav tier now carries no heading of its own, and without the
         reset it inherited "Verify" from the section above, so every result
         read "Verify · Overview". `section` is then falsy, and the dropdown
         omits the label entirely (`v-if="item.section"`). */
      section = null
    }
  })

  return flat
})

const results = computed(() => {
  const needle = query.value.trim().toLowerCase()

  if (!needle) {
    return []
  }

  return entries.value
    .filter(item => item.label.toLowerCase().includes(needle))
    .slice(0, 6)
})

const isOpen = computed(() => results.value.length > 0)

const reset = () => {
  query.value = ''
}

const go = item => {
  reset()
  inputEl.value?.blur()

  if (item.to) {
    router.push(item.to)
  } else if (item.href) {
    // `noreferrer` in the features string is what strips the Referer here — a
    // rel attribute can't apply to a programmatic open. It also severs
    // window.opener, which is the behaviour we want for anything external.
    window.open(item.href, item.target || '_self', 'noreferrer')
  }
}

const onEnter = () => {
  if (results.value.length) {
    go(results.value[0])
  }
}

const onKeydown = event => {
  if (event.ctrlKey && event.key === 'k') {
    event.preventDefault()
    inputEl.value?.focus()
  } else if (event.key === 'Escape') {
    reset()
    inputEl.value?.blur()
  }
}

const onDocumentClick = event => {
  if (root.value && !root.value.contains(event.target)) {
    reset()
  }
}

onMounted(() => {
  window.addEventListener('keydown', onKeydown)
  document.addEventListener('click', onDocumentClick)
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeydown)
  document.removeEventListener('click', onDocumentClick)
})
</script>

<template>
  <div ref="root" class="relative hidden md:block">
    <span class="absolute inset-y-0 left-3 flex items-center pointer-events-none text-gray-400">
      <icon :path="mdiMagnify" :size="18" w="w-5" h="h-5" />
    </span>
    <input
      ref="inputEl"
      v-model="query"
      type="search"
      aria-label="Jump to a page"
      :placeholder="t('nav.search')"
      class="h-9 w-56 lg:w-72 pl-10 pr-3 rounded-xl border text-sm transition-colors
             bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400
             dark:bg-gray-900 dark:border-gray-700 dark:text-gray-100 dark:placeholder-gray-500
             focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-opacity-40 focus:border-brand-500"
      @keydown.enter.prevent="onEnter"
    >

    <transition
      enter-active-class="transition duration-150 ease-out"
      enter-from-class="opacity-0 -translate-y-1"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 -translate-y-1"
    >
      <div
        v-if="isOpen"
        class="absolute left-0 mt-2 w-full min-w-[16rem] rounded-xl border shadow-card overflow-hidden z-40 py-1
               bg-white border-gray-200 dark:bg-gray-800 dark:border-gray-700"
      >
        <button
          v-for="item in results"
          :key="item.to || item.href"
          type="button"
          class="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors
                 text-gray-700 hover:bg-gray-50 hover:text-brand-600
                 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-brand-400
                 focus:outline-none focus-visible:bg-gray-50 dark:focus-visible:bg-gray-700"
          @click="go(item)"
        >
          <icon v-if="item.icon" :path="item.icon" :size="18" w="w-5" h="h-5" class="flex-none" />
          <span class="flex-grow truncate">{{ item.label }}</span>
          <span v-if="item.section" class="flex-none text-xs text-gray-400 dark:text-gray-500">
            {{ item.section }}
          </span>
          <icon
            v-if="item.target === '_blank'"
            :path="mdiOpenInNew"
            :size="14"
            w="w-4"
            h="h-4"
            class="flex-none text-gray-400"
          />
        </button>
      </div>
    </transition>
  </div>
</template>
